<section class="cc-page" [class.has-detail]="selectedOrder()">

  <!-- ── Header ───────────────────────────────────────────────────────────── -->
  <div class="page-header">
    <h1>Confirmed / Completed</h1>
  </div>

  <!-- ── Filter bar ────────────────────────────────────────────────────────── -->
  <div class="filter-bar">

    <!-- Search -->
    <div class="filter-search">
      <mat-icon class="search-icon">search</mat-icon>
      <input
        class="search-input"
        type="search"
        [(ngModel)]="searchQuery"
        placeholder="Search by order ID…"
        (keydown.enter)="onSearch()"
      />
    </div>

    <!-- City multi-select -->
    <div class="city-filter" [class.city-filter--open]="cityDropdownOpen">
      <button
        type="button"
        class="filter-btn"
        [class.filter-btn--active]="selectedCityCodes.length > 0"
        (click)="cityDropdownOpen = !cityDropdownOpen"
      >
        <mat-icon class="filter-btn-icon">location_on</mat-icon>
        @if (selectedCityCodes.length > 0) {
          Cities ({{ selectedCityCodes.length }})
        } @else {
          All cities
        }
        <mat-icon class="filter-btn-chevron">expand_more</mat-icon>
      </button>
      @if (cityDropdownOpen) {
        <div class="city-dropdown">
          @if (cities().length === 0) {
            <span class="city-dropdown-empty">No cities available</span>
          }
          @for (city of cities(); track city.code) {
            <label class="city-option">
              <input
                type="checkbox"
                [checked]="isCitySelected(city.code)"
                (change)="toggleCity(city.code)"
              />
              {{ city.label }}
            </label>
          }
        </div>
      }
    </div>

    <!-- Provider text filter -->
    <div class="filter-search filter-search--provider">
      <mat-icon class="search-icon">handyman</mat-icon>
      <input
        class="search-input"
        type="search"
        [(ngModel)]="providerQuery"
        placeholder="Filter by provider…"
      />
    </div>

    <!-- Status toggles -->
    <mat-button-toggle-group
      class="status-toggle-group"
      multiple
      [value]="selectedStatuses"
      (change)="onStatusChange($event.value)"
    >
      @for (s of pageStatuses; track s) {
        <mat-button-toggle [value]="s">{{ statusLabel(s) }}</mat-button-toggle>
      }
    </mat-button-toggle-group>

  </div>

  <!-- ── Body ─────────────────────────────────────────────────────────────── -->
  <div class="page-body">

    <!-- Table panel -->
    <div class="table-panel">
      @if (loading()) {
        <div class="loading">
          <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
        </div>
      } @else if (error()) {
        <div class="empty error-text">{{ error() }}</div>
      } @else {
        <div class="table-wrap">
          @if (filteredOrders().length) {
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th class="sort-th" [class.sort-th--active]="sortField === 'city_code'" (click)="toggleSort('city_code')">
                    City
                    <mat-icon class="sort-icon">{{ sortField === 'city_code' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</mat-icon>
                  </th>
                  <th>Provider</th>
                  <th>Service</th>
                  <th class="sort-th" [class.sort-th--active]="sortField === 'status'" (click)="toggleSort('status')">
                    Status
                    <mat-icon class="sort-icon">{{ sortField === 'status' ? (sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more' }}</mat-icon>
                  </th>
                  <th>Payment</th>
                  <th>Scheduled</th>
                  <th class="actions-th"></th>
                </tr>
              </thead>
              <tbody>
                @for (order of filteredOrders(); track order.id) {
                  <tr [class.row--selected]="selectedOrder()?.id === order.id" [class.row--closing]="closingOrderId() === order.id">
                    <td class="code-cell">{{ order.order_code }}</td>
                    <td class="name-cell">{{ customerName(order) }}</td>
                    <td class="city-cell">{{ order.city_code }}</td>
                    <td class="provider-cell">{{ providerLabel(order) }}</td>
                    <td class="service-cell">{{ order.service_name || order.category || '—' }}</td>
                    <td class="status-cell">
                      <span class="status-chip status-chip--{{ statusTone(order.status) }}">
                        {{ statusLabel(order.status) }}
                      </span>
                    </td>
                    <td class="payment-cell">
                      @if (order.payment_status) {
                        <span class="status-chip status-chip--{{ paymentStatusTone(order.payment_status) }}">
                          {{ paymentStatusLabel(order.payment_status) }}
                        </span>
                      } @else {
                        <span class="muted">—</span>
                      }
                    </td>
                    <td class="date-cell">{{ order.scheduled_at ? (order.scheduled_at | date: 'MMM d, y, HH:mm') : '—' }}</td>
                    <td class="actions-cell">
                      <button type="button" class="ghost-btn ghost-btn--small" (click)="openDetail(order)">Open</button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          } @else {
            <div class="empty">No orders found.</div>
          }
        </div>
      }
    </div>

    <!-- Detail panel -->
    @if (selectedOrder(); as order) {
      <div class="detail-panel">

        <!-- Header -->
        <div class="detail-header">
          <div class="detail-header-left">
            <span class="detail-order-code">{{ order.order_code }}</span>
            <span class="status-chip status-chip--{{ statusTone(order.status) }}">{{ statusLabel(order.status) }}</span>
          </div>
          <button type="button" class="detail-close-btn" aria-label="Close detail" (click)="closeDetail()">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <!-- Action bar -->
        <div class="detail-actions">
          <button type="button" class="ghost-btn ghost-btn--small ghost-btn--danger" (click)="requestClose(order)">
            <mat-icon>cancel</mat-icon>
            Close Order
          </button>
          <button type="button" class="ghost-btn ghost-btn--small" (click)="openTimeline(order)">
            <mat-icon>history</mat-icon>
            View Timeline
          </button>
          <button
            type="button"
            class="primary-teal-btn"
            [disabled]="chatInitiating()"
            (click)="initiateChatWithCustomer(order)"
          >
            <mat-icon>chat</mat-icon>
            {{ chatInitiating() ? 'Opening…' : 'Chat' }}
          </button>
        </div>
        @if (chatInitiateError()) {
          <div class="detail-action-error">{{ chatInitiateError() }}</div>
        }

        <div class="detail-body">

          <!-- ── Summary ──────────────────────────────────────────────────── -->
          <div class="section-card">
            <div class="section-header">
              <mat-icon class="section-icon">receipt_long</mat-icon>
              <h2 class="section-title">Order</h2>
            </div>
            <div class="fields-grid">

              <div class="field">
                <span class="field-label">City</span>
                <span class="field-value">{{ cityLabel(order.city_code) }}</span>
              </div>

              <div class="field">
                <span class="field-label">Service model</span>
                <span class="field-value">{{ serviceModelLabel(order.service_model) }}</span>
              </div>

              @if (order.category) {
                <div class="field">
                  <span class="field-label">Category</span>
                  <span class="field-value">{{ order.category }}</span>
                </div>
              }

              @if (order.service_name) {
                <div class="field">
                  <span class="field-label">Service</span>
                  <span class="field-value">{{ order.service_name }}</span>
                </div>
              }

              @if (order.scheduled_at) {
                <div class="field">
                  <span class="field-label">Scheduled</span>
                  <span class="field-value">{{ order.scheduled_at | date: 'MMM d, y, HH:mm' }}</span>
                </div>
              }

              @if (order.estimated_duration_minutes) {
                <div class="field">
                  <span class="field-label">Duration</span>
                  <span class="field-value">{{ order.estimated_duration_minutes }} min</span>
                </div>
              }

              <div class="field">
                <span class="field-label">Created</span>
                <span class="field-value field-value--muted">{{ order.created_at | date: 'MMM d, y, HH:mm' }}</span>
              </div>

              <div class="field">
                <span class="field-label">Updated</span>
                <span class="field-value field-value--muted">{{ order.updated_at | date: 'MMM d, y, HH:mm' }}</span>
              </div>

            </div>
          </div>

          <!-- ── Operator ─────────────────────────────────────────────────── -->
          <div class="section-card">
            <div class="section-header">
              <mat-icon class="section-icon">manage_accounts</mat-icon>
              <h2 class="section-title">Operator</h2>
            </div>
            @if (detailHistoryLoading()) {
              <div class="detail-history-loading">
                <mat-progress-spinner mode="indeterminate" diameter="18"></mat-progress-spinner>
              </div>
            } @else if (lastRelevantOperator(); as entry) {
              <div class="fields-stack">
                <div class="field">
                  <span class="field-label">Email</span>
                  <span class="field-value">{{ entry.changed_by_display_name || 'Operator' }}</span>
                </div>
                <div class="field">
                  <span class="field-label">Action</span>
                  <span class="field-value">
                    <span class="status-chip status-chip--{{ statusTone(entry.to_status) }}">{{ statusLabel(entry.to_status) }}</span>
                  </span>
                </div>
                <div class="field">
                  <span class="field-label">When</span>
                  <span class="field-value field-value--muted">{{ entry.created_at | date: 'MMM d, y, HH:mm' }}</span>
                </div>
              </div>
            } @else {
              <p class="operator-none">No operator action recorded.</p>
            }
          </div>

          <!-- ── Customer ─────────────────────────────────────────────────── -->
          <div class="section-card">
            <div class="section-header">
              <mat-icon class="section-icon">person</mat-icon>
              <h2 class="section-title">Customer</h2>
            </div>
            <div class="fields-stack">
              <div class="field">
                <span class="field-label">Name</span>
                <span class="field-value">{{ customerName(order) }}</span>
              </div>
              @if (order.handoff?.customer_phone) {
                <div class="field">
                  <span class="field-label">Phone</span>
                  <span class="field-value">{{ order.handoff!.customer_phone }}</span>
                </div>
              }
              @if (order.address) {
                <div class="field">
                  <span class="field-label">Service address</span>
                  <span class="field-value">{{ order.address }}</span>
                </div>
              }
              @if (order.address_access_notes) {
                <div class="field">
                  <span class="field-label">Access notes</span>
                  <span class="field-value">{{ order.address_access_notes }}</span>
                </div>
              }
            </div>
          </div>

          <!-- ── Provider ─────────────────────────────────────────────────── -->
          <div class="section-card">
            <div class="section-header">
              <mat-icon class="section-icon">handyman</mat-icon>
              <h2 class="section-title">Provider</h2>
            </div>
            <div class="fields-stack">
              @if (order.providers) {
                <div class="field">
                  <span class="field-label">Name</span>
                  <a class="field-link" [routerLink]="['/providers', order.providers.id]">{{ order.providers.name }}</a>
                </div>
                @if (order.providers.phone) {
                  <div class="field">
                    <span class="field-label">Phone</span>
                    <span class="field-value">{{ order.providers.phone }}</span>
                  </div>
                }
                @if (order.providers.email) {
                  <div class="field">
                    <span class="field-label">Email</span>
                    <span class="field-value">{{ order.providers.email }}</span>
                  </div>
                }
              } @else {
                <div class="field">
                  <span class="field-label">Assignment</span>
                  <span class="field-value field-value--muted">{{ providerLabel(order) }}</span>
                </div>
              }
              @if (order.provider_notes) {
                <div class="field">
                  <span class="field-label">Provider notes</span>
                  <span class="field-value">{{ order.provider_notes }}</span>
                </div>
              }
            </div>
          </div>

          <!-- ── Payment ──────────────────────────────────────────────────── -->
          <div class="section-card">
            <div class="section-header">
              <mat-icon class="section-icon">payments</mat-icon>
              <h2 class="section-title">Payment</h2>
            </div>
            <div class="fields-grid">

              <div class="field">
                <span class="field-label">Type</span>
                <span class="field-value">{{ paymentTypeLabel(order.payment_type) }}</span>
              </div>

              <div class="field">
                <span class="field-label">Status</span>
                @if (order.payment_status) {
                  <span class="status-chip status-chip--{{ paymentStatusTone(order.payment_status) }}">
                    {{ paymentStatusLabel(order.payment_status) }}
                  </span>
                } @else {
                  <span class="field-value field-value--muted">—</span>
                }
              </div>

              <div class="field">
                <span class="field-label">Quote</span>
                <span class="field-value">{{ centsToDollars(order.quote_amount_cents) }}</span>
              </div>

              <div class="field">
                <span class="field-label">Amount</span>
                <span class="field-value">{{ centsToDollars(order.payment_amount_cents) }}</span>
              </div>

              @if (order.stripe_payment_link_url) {
                <div class="field field--wide">
                  <span class="field-label">Payment link</span>
                  <a class="field-link" [href]="order.stripe_payment_link_url" target="_blank" rel="noopener">
                    {{ order.stripe_payment_link_url }}
                  </a>
                </div>
              }

            </div>
          </div>

          <!-- ── Notes ────────────────────────────────────────────────────── -->
          @if (order.internal_notes || order.completion_notes || order.cancelled_reason) {
            <div class="section-card">
              <div class="section-header">
                <mat-icon class="section-icon">notes</mat-icon>
                <h2 class="section-title">Notes</h2>
              </div>
              <div class="fields-stack">
                @if (order.internal_notes) {
                  <div class="field">
                    <span class="field-label">Internal notes</span>
                    <p class="notes-text">{{ order.internal_notes }}</p>
                  </div>
                }
                @if (order.completion_notes) {
                  <div class="field">
                    <span class="field-label">Completion notes</span>
                    <p class="notes-text">{{ order.completion_notes }}</p>
                  </div>
                }
                @if (order.cancelled_reason) {
                  <div class="field">
                    <span class="field-label">Cancelled reason</span>
                    <p class="notes-text">{{ order.cancelled_reason }}</p>
                  </div>
                }
              </div>
            </div>
          }

        </div>
      </div>
    }

  </div>

</section>

<!-- ── Confirm close modal ────────────────────────────────────────────────── -->
@if (confirmingClose(); as order) {
  <div class="modal-backdrop" (click)="cancelClose()">
    <div class="modal" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <mat-icon class="modal-icon modal-icon--danger">cancel</mat-icon>
        <h3 class="modal-title">Close order {{ order.order_code }}?</h3>
      </div>
      <p class="modal-body">
        This will mark the order as <strong>Closed</strong> and cannot be undone from this view.
      </p>
      @if (closeError()) {
        <p class="modal-error">{{ closeError() }}</p>
      }
      <div class="modal-actions">
        <button type="button" class="ghost-btn" (click)="cancelClose()" [disabled]="closing()">
          Cancel
        </button>
        <button type="button" class="ghost-btn ghost-btn--danger" (click)="confirmClose()" [disabled]="closing()">
          @if (closing()) {
            <mat-progress-spinner mode="indeterminate" diameter="16"></mat-progress-spinner>
          } @else {
            Close Order
          }
        </button>
      </div>
    </div>
  </div>
}

<!-- ── Timeline modal ────────────────────────────────────────────────────── -->
@if (timelineOrder(); as order) {
  <div class="modal-backdrop" (click)="closeTimeline()">
    <div class="modal modal--timeline" (click)="$event.stopPropagation()">
      <div class="modal-header">
        <mat-icon class="modal-icon">history</mat-icon>
        <h3 class="modal-title">Status history — {{ order.order_code }}</h3>
        <button type="button" class="detail-close-btn" (click)="closeTimeline()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      @if (timelineLoading()) {
        <div class="timeline-loading">
          <mat-progress-spinner mode="indeterminate" diameter="28"></mat-progress-spinner>
        </div>
      } @else if (timeline().length === 0) {
        <p class="timeline-empty">No history entries.</p>
      } @else {
        <div class="timeline-list">
          @for (entry of timeline(); track entry.id) {
            <div class="timeline-entry">
              <div class="timeline-dot"></div>
              <div class="timeline-content">
                <div class="timeline-top">
                  <span class="status-chip status-chip--{{ statusTone(entry.to_status) }}">{{ statusLabel(entry.to_status) }}</span>
                  <span class="timeline-actor">{{ historyActor(entry) }}</span>
                </div>
                <div class="timeline-date">{{ entry.created_at | date: 'MMM d, y, HH:mm' }}</div>
                @if (entry.reason) {
                  <div class="timeline-reason">{{ entry.reason }}</div>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>
  </div>
}
